import React, { useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { 
  Brain, Search, Filter, BarChart, Target, 
  Clock, CheckCircle, AlertTriangle, MessageSquare,
  Calendar, FileText, ChevronRight, Star, Upload
} from 'lucide-react';

const translations = {
  es: {
    title: 'Sistema de Asignación de Tareas IA',
    subtitle: 'Tareas y proyectos asignados por IA basados en tu rol y habilidades',
    sections: {
      assigned: 'Tareas Asignadas',
      completed: 'Completadas',
      upcoming: 'Próximas',
      suggested: 'Sugeridas por IA'
    },
    actions: {
      submitTask: 'Enviar tarea',
      startTask: 'Comenzar tarea',
      viewFeedback: 'Ver feedback'
    },
    search: 'Buscar tareas...',
    filters: 'Filtros',
    status: {
      pending: 'Pendiente',
      inProgress: 'En progreso',
      submitted: 'Enviado',
      completed: 'Completado'
    },
    feedback: {
      title: 'Feedback de la IA',
      analyzing: 'Analizando tu envío...'
    }
  },
  en: {
    title: 'AI Task Assignment System',
    subtitle: 'AI-assigned tasks and projects based on your role and skills',
    sections: {
      assigned: 'Assigned Tasks',
      completed: 'Completed',
      upcoming: 'Upcoming',
      suggested: 'AI Suggested'
    },
    actions: {
      submitTask: 'Submit Task',
      startTask: 'Start Task',
      viewFeedback: 'View Feedback'
    },
    search: 'Search tasks...',
    filters: 'Filters',
    status: {
      pending: 'Pending',
      inProgress: 'In Progress',
      submitted: 'Submitted',
      completed: 'Completed'
    },
    feedback: {
      title: 'AI Feedback',
      analyzing: 'Analyzing your submission...'
    }
  }
};

// Mock data
const mockTasks = [
  {
    id: 1,
    title: 'Análisis de Requisitos del Cliente',
    description: 'Realizar un análisis detallado de los requisitos del cliente para el proyecto de e-commerce.',
    type: 'analysis',
    priority: 'high',
    dueDate: '2025-03-20',
    status: 'in-progress',
    aiScore: 85,
    skills: ['Análisis', 'Documentación', 'Comunicación'],
    feedback: {
      strengths: [
        'Excelente estructura de análisis',
        'Buena identificación de stakeholders'
      ],
      improvements: [
        'Considerar más casos de uso',
        'Profundizar en requisitos no funcionales'
      ]
    }
  },
  {
    id: 2,
    title: 'Diseño de Arquitectura de Sistema',
    description: 'Crear la arquitectura de sistema para la nueva plataforma de gestión de inventario.',
    type: 'design',
    priority: 'medium',
    dueDate: '2025-03-25',
    status: 'pending',
    aiScore: null,
    skills: ['Arquitectura', 'Diseño de Sistemas', 'Documentación Técnica']
  }
];

const mockSuggestedTasks = [
  {
    id: 'sug-1',
    title: 'Optimización de Rendimiento',
    description: 'La IA ha detectado oportunidades de mejora en el rendimiento del sistema.',
    relevance: 95,
    difficulty: 'intermediate',
    estimatedTime: '4 hours'
  },
  {
    id: 'sug-2',
    title: 'Documentación de API',
    description: 'Actualizar la documentación de la API con los últimos endpoints.',
    relevance: 88,
    difficulty: 'beginner',
    estimatedTime: '3 hours'
  }
];

const AITaskAssignmentPage = () => {
  const [activeSection, setActiveSection] = useState('assigned');
  const [selectedTask, setSelectedTask] = useState<any | null>(null);
  const [submission, setSubmission] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { language } = useLanguage();
  const t = translations[language];

  const handleSubmitTask = async () => {
    if (!submission.trim()) return;
    
    setSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    setSubmitting(false);
    setSelectedTask(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t.title}</h1>
            <p className="text-foreground/70">{t.subtitle}</p>
          </div>
          <button className="inline-flex items-center text-accent hover:text-accent/90">
            <MessageSquare className="h-5 w-5 mr-2" />
            Consultar IA
          </button>
        </div>

        {/* Search and Filters */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 sm:space-x-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder={t.search}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-foreground/50" />
          </div>
          <div className="flex space-x-4">
            <button className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary/30">
              <Filter className="h-4 w-4 mr-2" />
              {t.filters}
            </button>
            <button className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary/30">
              <BarChart className="h-4 w-4 mr-2" />
              Analytics
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-border mb-8">
          <nav className="-mb-px flex space-x-8">
            {Object.entries(t.sections).map(([key, value]) => (
              <button
                key={key}
                onClick={() => setActiveSection(key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeSection === key
                    ? 'border-accent text-accent'
                    : 'border-transparent text-foreground/70 hover:text-foreground hover:border-border'
                }`}
              >
                {value}
              </button>
            ))}
          </nav>
        </div>

        {/* Content Section */}
        {activeSection === 'assigned' && (
          <div className="space-y-6">
            {mockTasks.map((task) => (
              <div
                key={task.id}
                className="dark-card p-6 hover:border-accent/50 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                      {task.type === 'analysis' ? (
                        <Target className="h-5 w-5 text-accent" />
                      ) : (
                        <FileText className="h-5 w-5 text-accent" />
                      )}
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-foreground">{task.title}</h3>
                      <p className="text-foreground/70">{task.description}</p>
                    </div>
                  </div>
                  <div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      task.priority === 'high'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {task.priority.toUpperCase()}
                    </span>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {task.skills.map((skill, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent/10 text-accent"
                    >
                      {skill}
                    </span>
                  ))}
                </div>

                <div className="mt-4 grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-foreground/70">Due Date</p>
                    <p className="text-sm font-medium text-foreground">{task.dueDate}</p>
                  </div>
                  <div>
                    <p className="text-sm text-foreground/70">Status</p>
                    <p className="text-sm font-medium text-foreground">{task.status}</p>
                  </div>
                  {task.aiScore && (
                    <div>
                      <p className="text-sm text-foreground/70">AI Score</p>
                      <p className="text-sm font-medium text-foreground">{task.aiScore}/100</p>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex justify-end space-x-4">
                  {task.status === 'completed' ? (
                    <button className="inline-flex items-center text-accent hover:text-accent/90">
                      {t.actions.viewFeedback}
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </button>
                  ) : (
                    <button
                      onClick={() => setSelectedTask(task)}
                      className="inline-flex items-center text-accent hover:text-accent/90"
                    >
                      {task.status === 'in-progress' ? t.actions.submitTask : t.actions.startTask}
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </button>
                  )}
                </div>

                {task.feedback && (
                  <div className="mt-4 bg-accent/5 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-foreground mb-2">
                      {t.feedback.title}
                    </h4>
                    <div className="space-y-2">
                      <div>
                        <p className="text-sm font-medium text-green-600">Fortalezas:</p>
                        <ul className="mt-1 text-sm text-foreground/70">
                          {task.feedback.strengths.map((strength, index) => (
                            <li key={index} className="flex items-center">
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                              {strength}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-yellow-600">Áreas de mejora:</p>
                        <ul className="mt-1 text-sm text-foreground/70">
                          {task.feedback.improvements.map((improvement, index) => (
                            <li key={index} className="flex items-center">
                              <AlertTriangle className="h-4 w-4 text-yellow-500 mr-2" />
                              {improvement}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {activeSection === 'suggested' && (
          <div className="space-y-6">
            {mockSuggestedTasks.map((task) => (
              <div
                key={task.id}
                className="dark-card p-6 hover:border-accent/50 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <Brain className="h-5 w-5 text-accent" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-foreground">{task.title}</h3>
                      <p className="text-foreground/70">{task.description}</p>
                    </div>
                  </div>
                  <div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      <Star className="h-4 w-4 mr-1" />
                      {task.relevance}% Match
                    </span>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-foreground/70">Difficulty</p>
                    <p className="text-sm font-medium text-foreground">{task.difficulty}</p>
                  </div>
                  <div>
                    <p className="text-sm text-foreground/70">Estimated Time</p>
                    <p className="text-sm font-medium text-foreground">{task.estimatedTime}</p>
                  </div>
                  <div className="flex justify-end">
                    <button className="inline-flex items-center text-accent hover:text-accent/90">
                      Accept Task
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Task Submission Modal */}
      {selectedTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-background rounded-lg max-w-2xl w-full">
            <div className="p-6 border-b border-border">
              <div className="flex justify-between items-start">
                <h3 className="text-lg font-medium text-foreground">{selectedTask.title}</h3>
                <button
                  onClick={() => setSelectedTask(null)}
                  className="text-foreground/50 hover:text-foreground"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-foreground/80 mb-1">
                  Tu respuesta
                </label>
                <textarea
                  rows={6}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="Describe tu solución..."
                  value={submission}
                  onChange={(e) => setSubmission(e.target.value)}
                ></textarea>
              </div>

              <div className="mb-4">
                <button className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary/30">
                  <Upload className="h-4 w-4 mr-2" />
                  Adjuntar archivos
                </button>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setSelectedTask(null)}
                  className="px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary/30"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmitTask}
                  disabled={!submission.trim() || submitting}
                  className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t.feedback.analyzing}
                    </>
                  ) : (
                    t.actions.submitTask
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AITaskAssignmentPage;